/**
 * Client view of the signed-in account's entitlements (owned RPGPublisher books),
 * served by the BFF `GET /me/entitlements` (which proxies Atlas). Anonymous users
 * own nothing — the query stays disabled and resolves to an empty list, so the
 * encyclopedia gate treats them as non-owners (padlock → buy / sign-in).
 *
 * This is UI gating only. The authoritative unlock decision at the point of
 * purchase/download must re-check Atlas (`POST /accounts/entitlements/check`);
 * never trust this snapshot for that.
 */
import { useQuery } from '@tanstack/react-query'
import { app } from '@/lib/api/client'
import { useUserStore } from '@/stores/userStore'
import { getActiveWorldConfig } from '@/lib/worldConfig'

export interface Entitlement {
  rpgBookId: string
  skuCode?: string
  title?: string
  campaignId?: string
}

interface EntitlementsResponse {
  entitlements: Entitlement[]
  bookIds: string[]
}

export function useEntitlements() {
  const authenticated = useUserStore((s) => s.status === 'authenticated')
  const query = useQuery({
    queryKey: ['me', 'entitlements'],
    queryFn: () => app.get<EntitlementsResponse>('/me/entitlements'),
    enabled: authenticated,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
  return {
    bookIds: query.data?.bookIds ?? [],
    entitlements: query.data?.entitlements ?? [],
    isLoading: authenticated && query.isLoading,
    isAuthenticated: authenticated,
  }
}

/** Whether the current account owns a specific book. */
export function useOwnsBook(bookId: string | undefined): boolean {
  const { bookIds } = useEntitlements()
  return !!bookId && bookIds.includes(bookId)
}

/**
 * Whether the visitor owns the active world's primary (fallback) unlock book.
 * Prefer {@link useOwnedBookIds} + `isEntityLockedForOwner` for per-card locks,
 * or {@link useHasAnyGrymwyrdAccess} for the facet-level gate.
 */
export function useHasGrymwyrdAccess(): boolean {
  return useOwnsBook(getActiveWorldConfig().fallbackUnlockBookId)
}

/** The viewer's owned book ids — feed to `isEntityLockedForOwner` for per-book locks. */
export function useOwnedBookIds(): string[] {
  return useEntitlements().bookIds
}

/**
 * Whether the viewer owns at least one Grymwyrd unlock book (Cataclysm or
 * Galvanist). Drives the facet-level full-page gate: owning either book lets the
 * visitor browse the Grymwyrd slice with per-card locks, rather than being
 * blocked wholesale.
 */
export function useHasAnyGrymwyrdAccess(): boolean {
  const { bookIds } = useEntitlements()
  const { unlockBookIds } = getActiveWorldConfig()
  return bookIds.some((id) => unlockBookIds.includes(id))
}
